import { getStringHash } from '@noths/polaris-client-utils';
import type { Environment } from '@noths/polaris-server-content-fetching';
import { fetchContentGraphQL, stackConfig } from '@noths/polaris-server-content-fetching';

import {
  INSPIRATION_CONTENTSTACK_CACHE_TTL_SECONDS,
  inspirationAllowCaching,
} from 'src/constants/inspirationCache';
import { withRedisFetchCache } from 'src/services/withRedisFetchCache';
import { logger } from 'src/utils/serverLogger';
import { getContentstackBranch } from './getContentstackBranch';
import {
  INSPIRATION_LIBRARY_LIMIT,
  inspirationLibraryQuery,
} from './queries/inspirationLibraryQuery.graphql';
import { getContentstackEnvironment } from './utils';

const stackName = 'DX' as const;

const INSPIRATION_LIBRARY_QUERY_HASH = getStringHash(inspirationLibraryQuery);
// A cold list request measured about 500ms, which leaves the package default of
// 1000ms little headroom. This sits on the page render path, so it stays well
// short of the 8000ms the sitemap fetches use.
const INSPIRATION_LIBRARY_REQUEST_TIMEOUT_MS = 1500;

const cacheKey = (env: Environment, branch: string) =>
  `inspiration-library-v1-${env}-${stackName}-${branch}-${INSPIRATION_LIBRARY_QUERY_HASH}`;

export interface InspirationTermRef {
  taxonomyUid: string;
  termUid: string;
}

export interface InspirationPostContent {
  departmentUrls: string[];
  description: string | null;
  imageUrl: string | null;
  publishedDate: string | null;
  terms: InspirationTermRef[];
  title: string;
  url: string;
}

interface GqlConnection<T> {
  edges?: Array<{ node?: T | null } | null> | null;
}

/** A taxonomy term as an entry carries it, in REST and GraphQL alike. */
export interface ServiceTaxonomyTermRef {
  taxonomy_uid?: string | null;
  term_uid?: string | null;
}

export interface GqlInspirationRow {
  canonical_imageConnection?: GqlConnection<{ url?: string | null }> | null;
  department_referenceConnection?: GqlConnection<{ url?: string | null }> | null;
  published_date?: string | null;
  seo?: { description?: string | null } | null;
  taxonomies?: Array<ServiceTaxonomyTermRef | null> | null;
  title?: string | null;
  url?: string | null;
}

interface InspirationLibraryGqlData {
  all_inspiration?: {
    items: GqlInspirationRow[] | null;
    total?: number;
  };
}

const nonEmpty = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

const connectionUrls = (connection: GqlConnection<{ url?: string | null }> | null | undefined) =>
  (connection?.edges ?? [])
    .map((edge) => nonEmpty(edge?.node?.url))
    .filter((url): url is string => url !== null);

export const toInspirationTermRefs = (
  taxonomies: Array<ServiceTaxonomyTermRef | null> | null | undefined,
): InspirationTermRef[] =>
  (taxonomies ?? []).flatMap((taxonomy) => {
    const taxonomyUid = nonEmpty(taxonomy?.taxonomy_uid);
    const termUid = nonEmpty(taxonomy?.term_uid);

    return taxonomyUid && termUid ? [{ taxonomyUid, termUid }] : [];
  });

export const mapInspirationRow = (row: GqlInspirationRow): InspirationPostContent | null => {
  const url = nonEmpty(row.url);
  const title = nonEmpty(row.title);

  if (!url || !title) {
    return null;
  }

  return {
    departmentUrls: connectionUrls(row.department_referenceConnection),
    description: nonEmpty(row.seo?.description),
    imageUrl: connectionUrls(row.canonical_imageConnection)[0] ?? null,
    publishedDate: nonEmpty(row.published_date),
    terms: toInspirationTermRefs(row.taxonomies),
    title,
    url,
  };
};

/** Newest first. Undated posts sort last, so they sink while tagging is incomplete. */
export const compareByPublishedDateDesc = (
  a: InspirationPostContent,
  b: InspirationPostContent,
) => {
  if (a.publishedDate === b.publishedDate) {
    return 0;
  }
  if (a.publishedDate === null) {
    return 1;
  }
  if (b.publishedDate === null) {
    return -1;
  }

  return a.publishedDate < b.publishedDate ? 1 : -1;
};

const fetchInspirationLibraryUncached = async (
  env: Environment,
  branch: string,
): Promise<InspirationPostContent[]> => {
  const { name, token } = stackConfig[stackName].envs[env];

  const result = await fetchContentGraphQL(
    {
      deliveryToken: token,
      environment: name,
      query: inspirationLibraryQuery,
      stackAPIKey: stackConfig[stackName].apiKey,
      variables: { limit: INSPIRATION_LIBRARY_LIMIT },
    },
    { branch, timeout: INSPIRATION_LIBRARY_REQUEST_TIMEOUT_MS },
  );

  if (result.status === 'error') {
    const message = result.content;
    throw new Error(
      `Inspiration library GraphQL fetch failed: ${
        message instanceof Error ? message.message : String(message)
      }`,
    );
  }

  const data = result.content as InspirationLibraryGqlData | undefined;
  const items = data?.all_inspiration?.items ?? [];
  const total = data?.all_inspiration?.total ?? items.length;

  if (total > items.length) {
    logger.warn(
      'Inspiration library exceeds a single GraphQL request, newest posts may be missing',
      {
        env,
        returned: items.length,
        total,
      },
    );
  }

  return items
    .map(mapInspirationRow)
    .filter((post): post is InspirationPostContent => post !== null)
    .sort(compareByPublishedDateDesc);
};

/** Every published Inspiration post, newest first. */
export const fetchInspirationLibrary = async (
  isPreview?: boolean,
): Promise<InspirationPostContent[]> => {
  const env = getContentstackEnvironment(isPreview);
  const branch = getContentstackBranch();

  return withRedisFetchCache({
    allowCaching: inspirationAllowCaching(env),
    cacheKey: cacheKey(env, branch),
    cacheTTLInSeconds: INSPIRATION_CONTENTSTACK_CACHE_TTL_SECONDS,
    fallbackOnError: [] as InspirationPostContent[],
    fetcher: () => fetchInspirationLibraryUncached(env, branch),
  });
};
